import React, { useState, useEffect } from 'react';
import { 
  TextField, 
  Button, 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Paper, 
  Alert,
  TableContainer, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from 'sweetalert2';
import metaApi from '../../../API/meta-manage-api/meta.api';

// --- COMPONENT: SeoMetadataTable (The Table View with Pagination) ---
const SeoMetadataTable = ({ 
  data, 
  onEdit, 
  onDelete, 
  page, 
  rowsPerPage, 
  totalCount, 
  onPageChange, 
  onRowsPerPageChange,
  loading 
}) => {
  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom sx={{ borderBottom: '1px solid #ddd', pb: 1, fontWeight: 'bold' }}>
        Existing SEO Records {totalCount > 0 && `(${totalCount})`}
      </Typography>
      
      <TableContainer component={Paper} elevation={1}>
        <Table sx={{ minWidth: 650 }} aria-label="seo metadata table">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Page Key (Route)</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Meta Title</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Meta Description</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="textSecondary">
                    No SEO records found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">{row.id}</TableCell>
                  <TableCell>{row.page_key}</TableCell>
                  <TableCell>{row.title}</TableCell>
                  <TableCell sx={{ fontSize: '0.85rem', maxWidth: 300 }}>
                    {row.description?.length > 60 ? row.description.substring(0, 60) + '...' : row.description}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => onEdit(row)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => onDelete(row.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[10, 25, 50]}
        labelRowsPerPage="Rows per page:"
      />
    </Box>
  );
};


// --- COMPONENT: SeoManagerForm (The Creation/Edit Form) ---
const initialFormState = {
  page_key: '',
  title: '',
  description: '',
  keywords: '',
  og_title: '',
  og_description: '',
  // Auto-populated defaults
  locale: 'en',
  canonical: '',
  robots: 'index, follow',
  og_image: '',
  extra: '{}',
};

const SeoManagerForm = ({ onDataSaved, editData = null, onCancel }) => {
  const [formData, setFormData] = useState(editData || initialFormState);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editData) {
      setFormData({
        ...initialFormState,
        ...editData
      });
    }
  }, [editData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error for the field being edited
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  // Auto-fill OG fields from Meta fields on blur
  const handleMetaTitleBlur = () => {
    if (!formData.og_title && formData.title) {
      setFormData(prev => ({ ...prev, og_title: formData.title }));
    }
  };

  const handleMetaDescriptionBlur = () => {
    if (!formData.og_description && formData.description) {
      setFormData(prev => ({ ...prev, og_description: formData.description }));
    }
  };

  const validate = () => {
    let tempErrors = {};
    let isValid = true;

    if (!formData.page_key || formData.page_key.trim() === '') {
      tempErrors.page_key = "Page Key is required.";
      isValid = false;
    } else if (formData.page_key.length > 255) {
      tempErrors.page_key = "Page Key cannot exceed 255 characters.";
      isValid = false;
    }

    if (!formData.title || formData.title.trim() === '') {
      tempErrors.title = "Meta Title is required.";
      isValid = false;
    }
    // Note: No hard validation for length - soft warning only via helper text

    if (!formData.description || formData.description.trim() === '') {
      tempErrors.description = "Meta Description is required.";
      isValid = false;
    }
    // Note: No hard validation for length - soft warning only via helper text

    setErrors(tempErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      setMessage({ type: 'error', text: 'Please correct the highlighted errors.' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const payload = {
        ...formData,
        locale: formData.locale || 'en',
        robots: formData.robots || 'index, follow',
        canonical: formData.canonical || '',
        og_image: formData.og_image || '',
        extra: formData.extra || '{}',
      };

      let response;
      if (editData) {
        // Update existing
        response = await metaApi.updateMetaTags(editData.id, payload);
      } else {
        // Create new
        response = await metaApi.addMetaTags(payload);
      }

      if (response.status === 200 || response.status === 201) {
        setMessage({ 
          type: 'success', 
          text: editData ? 'Meta tags updated successfully!' : 'Meta tags added successfully!' 
        });
        setFormData(initialFormState);
        setTimeout(() => {
          onDataSaved();
        }, 1000);
      } else {
        setMessage({ type: 'error', text: 'Failed to save meta tags.' });
      }
    } catch (error) {
      console.error('Error saving meta tags:', error);
      setMessage({ 
        type: 'error', 
        text: error?.response?.data?.message || 'An error occurred while saving.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const renderTextField = (name, label, options = {}) => {
    const { 
      multiline = false, 
      rows = 1, 
      required = false, 
      onBlur,
      maxLength,
      showCounter = false,
      helperText: customHelperText
    } = options;
    
    const value = formData[name] || '';
    const counterText = showCounter ? `${value.length}${maxLength ? `/${maxLength}` : ''} chars` : '';
    
    // Show warning (not error) when exceeding recommended length
    const showLengthWarning = maxLength && value.length > maxLength;
    const warningColor = showLengthWarning ? '#ed6c02' : 'inherit'; // Orange warning color
    
    // Build helper text: error > warning > custom > counter
    let helperTextComponent = null;
    if (errors[name]) {
      helperTextComponent = errors[name];
    } else if (showLengthWarning) {
      helperTextComponent = (
        <span style={{ color: warningColor }}>
          {counterText} — Exceeds recommended length. May be truncated in search results.
        </span>
      );
    } else if (customHelperText) {
      helperTextComponent = (
        <span>
          {counterText && <span style={{ fontWeight: 500 }}>{counterText}</span>}
          {counterText && ' — '}
          {customHelperText}
        </span>
      );
    } else if (counterText) {
      helperTextComponent = counterText;
    }
    
    return (
      <TextField
        fullWidth
        required={required}
        label={label}
        name={name}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        error={!!errors[name]}
        helperText={helperTextComponent}
        multiline={multiline}
        rows={rows}
        margin="normal"
        variant="outlined"
        sx={{ 
          mb: 2,
          '& .MuiFormHelperText-root': {
            color: showLengthWarning ? warningColor : 'inherit'
          }
        }}
      />
    );
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h5" component="h1" gutterBottom align="center" sx={{ fontWeight: 'bold', mb: 2 }}>
        {editData ? 'Edit SEO Metadata' : 'Create SEO Metadata'}
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <Box component="form" noValidate>
        {/* --- Section 1: Page Information --- */}
        <Typography variant="h6" gutterBottom sx={{ mt: 2, borderBottom: '1px solid #ddd', pb: 1, color: 'primary.main' }}>
          Page Information
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderTextField('page_key', 'Page Key (Route Name)', { 
              required: true,
              helperText: errors.page_key || "e.g., home.index, about-us, services.cardiology"
            })}
          </Grid>
        </Grid>

        {/* --- Section 2: Search Engine Meta --- */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3, borderBottom: '1px solid #ddd', pb: 1, color: 'primary.main' }}>
          Search Engine Meta
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {renderTextField('title', 'Meta Title', { 
              required: true, 
              maxLength: 60, 
              showCounter: true,
              onBlur: handleMetaTitleBlur,
              helperText: "Recommended: 60 chars or less. Search engines typically display ~60 characters in results."
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderTextField('keywords', 'Meta Keywords', {
              helperText: "Comma-separated keywords (e.g., healthcare, hospital, cardiology)"
            })}
          </Grid>
          <Grid item xs={12}>
            {renderTextField('description', 'Meta Description', { 
              multiline: true, 
              rows: 3, 
              required: true, 
              maxLength: 160, 
              showCounter: true,
              onBlur: handleMetaDescriptionBlur,
              helperText: "Recommended: 160 chars or less. Longer descriptions may be truncated with '...' in search results."
            })}
          </Grid>
        </Grid>
        
        {/* --- Section 3: Open Graph (Social Media) --- */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3, borderBottom: '1px solid #ddd', pb: 1, color: 'primary.main' }}>
          Social Media Meta (Open Graph)
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {renderTextField('og_title', 'OG Title (Social Title)', {
              helperText: "Title shown when shared on social platforms"
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderTextField('og_description', 'OG Description (Social Description)', { 
              multiline: true, 
              rows: 2,
              helperText: "Description shown when shared on social platforms"
            })}
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          {onCancel && (
            <Button 
              type="button" 
              variant="outlined" 
              size="large"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          )}
          <Button 
            type="button" 
            variant="contained" 
            color="primary" 
            size="large"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
          >
            {loading ? 'Saving...' : (editData ? 'Update' : 'Save')} SEO Metadata
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};


// --- EXPORTED MAIN APP COMPONENT (The Dashboard Container) ---
const SeoMetaIndexPage = () => {
    const [seoData, setSeoData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0); // MUI TablePagination uses 0-based
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [editData, setEditData] = useState(null);

    // Fetch data on mount and when pagination changes
    useEffect(() => {
      fetchSeoData();
    }, [page, rowsPerPage]);

    const fetchSeoData = async () => {
      setLoading(true);
      try {
        // Convert 0-based MUI page to 1-based API page
        const apiPage = page + 1;
        const response = await metaApi.fetchTypes(apiPage, rowsPerPage);
        
        if (response.data && response.data.data) {
          setSeoData(response.data.data);
          // Handle different pagination response formats
          if (response.data.meta && response.data.meta.total) {
            setTotalCount(response.data.meta.total);
          } else if (response.data.total !== undefined) {
            setTotalCount(response.data.total);
          } else {
            setTotalCount(response.data.data.length);
          }
        } else {
          setSeoData([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.error('Error fetching SEO data:', error);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to fetch SEO data. Please try again.',
          icon: 'error',
          confirmButtonText: 'Ok'
        });
        setSeoData([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    const handleOpen = () => {
      setEditData(null);
      setOpen(true);
    };

    const handleClose = () => {
      setOpen(false);
      setEditData(null);
    };

    const handleDataSaved = () => {
      fetchSeoData();
      handleClose();
    };

    const handleEdit = (row) => {
      setEditData(row);
      setOpen(true);
    };

    const handleDelete = async (id) => {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'This SEO record will be permanently deleted!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel'
      });

      if (result.isConfirmed) {
        try {
          const response = await metaApi.deleteMetaTags(id);
          if (response.status === 200 || response.status === 204) {
            Swal.fire({
              title: 'Deleted!',
              text: 'SEO record has been deleted.',
              icon: 'success',
              confirmButtonText: 'Ok'
            });
            fetchSeoData();
          } else {
            throw new Error('Failed to delete');
          }
        } catch (error) {
          console.error('Error deleting SEO record:', error);
          Swal.fire({
            title: 'Error!',
            text: 'Failed to delete the SEO record. Please try again.',
            icon: 'error',
            confirmButtonText: 'Ok'
          });
        }
      }
    };

    const handleChangePage = (event, newPage) => {
      setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0); // Reset to first page when changing rows per page
    };

    return (
        <Container maxWidth="xl" sx={{ pt: 2, pb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              SEO Meta Manager
            </Typography>
            <Button 
              variant="contained" 
              onClick={handleOpen} 
              size="large"
            >
              + Add Meta Data
            </Button>
          </Box>

          {/* SEO Metadata Table with Pagination */}
          <SeoMetadataTable 
            data={seoData}
            onEdit={handleEdit}
            onDelete={handleDelete}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            loading={loading}
          />

          {/* Add/Edit Modal */}
          <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="md" 
            fullWidth
            scroll="body"
          >
            <DialogTitle sx={{ fontWeight: 'bold' }}>
              {editData ? 'Edit SEO Metadata' : 'Add New SEO Metadata'}
            </DialogTitle>
            <DialogContent>
              <SeoManagerForm 
                onDataSaved={handleDataSaved} 
                editData={editData}
                onCancel={handleClose}
              />
            </DialogContent>
          </Dialog>
        </Container>
    );
};

export default SeoMetaIndexPage;